// Fix GZ (2026-10-19): "Debe generar un estado de cuentas a partir de estas cargas, pendientes de cobros y
// cobrados, que se puedan visualizar por cliente o general, con antigüedad de la deuda si corresponde"
// (CEN, Claudio). Lee GET /api/treasury/cuentas-por-cobrar/antiguedad: un resumen por cliente con los tramos
// de antigüedad y el detalle factura por factura. Cada moneda se suma por separado (USD aparte, en azul).
import { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw, Download, FileText, Eye } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatUSD2, formatDate } from "../../utils/format";
import { exportarReporte, fechaReporte } from "../../utils/reportes";
import EstadoCuentaModal from "../../components/EstadoCuentaModal";

const TRAMO_BG = { al_dia: "bg-emerald-500", d1_30: "bg-yellow-400", d31_60: "bg-orange-400", d61_90: "bg-red-400", d91_mas: "bg-red-700" };
const TRAMO_TXT = { al_dia: "text-emerald-700", d1_30: "text-yellow-700", d31_60: "text-orange-600", d61_90: "text-red-600", d91_mas: "text-red-800" };
const ESTADO = {
  pendiente: ["Pendiente", "bg-amber-100 text-amber-700 border-amber-200"],
  parcial: ["Cobro parcial", "bg-blue-100 text-blue-700 border-blue-200"],
  vencida: ["Vencida", "bg-red-100 text-red-700 border-red-200"],
  cobrada: ["Cobrada", "bg-emerald-100 text-emerald-700 border-emerald-200"],
};
const CONDICION = { contado: "Contado", credito: "Crédito" };

// Un monto en Gs. y, aparte, el de las facturas en USD (cada moneda se suma sola).
function Monto({ v }) {
  const gs = Number(v?.gs) || 0;
  const usd = Number(v?.usd) || 0;
  if (!gs && !usd) return <span className="text-gray-300">—</span>;
  return (
    <>
      {gs > 0 && <span>{formatPYG(gs)}</span>}
      {usd > 0 && <span className="block text-[11px] font-semibold text-blue-600">{formatUSD2(usd)}</span>}
    </>
  );
}

function Tarjeta({ titulo, color, sub, testId, children }) {
  const cls = { amber: "bg-amber-50 border-amber-100", red: "bg-red-50 border-red-100", emerald: "bg-emerald-50 border-emerald-100", slate: "bg-slate-50 border-slate-200" }[color];
  return (
    <div className={`rounded-2xl border p-4 ${cls}`} data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{titulo}</p>
      <div className="text-xl font-bold mt-1 text-gray-900">{children}</div>
      {sub && <p className="text-xs mt-1 text-gray-500">{sub}</p>}
    </div>
  );
}

function Exportar({ exportar, exportando, testId }) {
  const base = "flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border disabled:opacity-50";
  return (
    <div className="flex gap-2">
      <button onClick={() => exportar("excel")} disabled={!!exportando} className={`${base} border-emerald-600 text-emerald-700 hover:bg-emerald-50`} data-testid={`${testId}-excel`}><Download size={13} /> Excel</button>
      <button onClick={() => exportar("pdf")} disabled={!!exportando} className={`${base} border-gray-300 text-gray-700 hover:bg-gray-50`} data-testid={`${testId}-pdf`}><FileText size={13} /> PDF</button>
      <button onClick={() => exportar("ver")} disabled={!!exportando} className={`${base} border-gray-300 text-gray-700 hover:bg-gray-50`} data-testid={`${testId}-preview`}><Eye size={13} /> Vista previa</button>
    </div>
  );
}

export default function EstadoCuentaTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState("");        // "" = todos los clientes
  const [estado, setEstado] = useState("pendientes"); // pendientes | cobradas | todas
  const [exportando, setExportando] = useState("");
  const [cuenta, setCuenta] = useState(null);         // cliente cuya cuenta corriente se abre

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/treasury/cuentas-por-cobrar/antiguedad`, { withCredentials: true });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar el estado de cuenta");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (data && cliente && !data.clientes.some(c => c.cliente_clave === cliente)) setCliente(""); }, [data, cliente]);

  if (loading && !data) return <div className="py-16 flex justify-center" data-testid="ec-tab"><Loader2 className="animate-spin text-gray-400" /></div>;
  if (!data) return <div className="py-16 text-center text-sm text-gray-400" data-testid="ec-tab">No se pudo cargar el estado de cuenta</div>;

  const clientes = data.clientes || [];
  const filas = cliente ? clientes.filter(c => c.cliente_clave === cliente) : clientes;
  const tot = cliente ? filas[0] || data.totales : data.totales;
  const detalle = (data.detalle || []).filter(r => (!cliente || r.cliente_clave === cliente)
    && (estado === "todas" || (estado === "cobradas" ? r.estado === "cobrada" : r.estado !== "cobrada")));
  const pendGs = tot.pendiente.gs;
  const pct = (v) => (pendGs > 0 ? Math.round((v * 1000) / pendGs) / 10 : 0);
  const tramoLabel = Object.fromEntries(data.tramos.map(t => [t.key, t.label]));

  const exportarResumen = (tipo) => exportarReporte(tipo, {
    titulo: "Estado de cuenta de clientes — antigüedad de la deuda",
    filtros: [`Al ${fechaReporte(data.fecha_corte)}`, cliente && `Cliente ${filas[0]?.cliente || ""}`].filter(Boolean).join(" · "),
    columnas: [{ l: "Cliente" }, { l: "RUC" }, { l: "Facturas pendientes" },
      ...data.tramos.map(t => ({ l: `${t.label} (Gs)`, num: true })),
      { l: "Pendiente (Gs)", num: true }, { l: "Pendiente USD", num: true }, { l: "Cobrado (Gs)", num: true }],
    filas: filas.map(c => [c.cliente, c.ruc || "", c.pendientes, ...data.tramos.map(t => c.tramos[t.key].gs), c.pendiente.gs, c.pendiente.usd, c.cobrado.gs]),
    totales: cliente ? null : ["TOTALES", "", tot.pendientes, ...data.tramos.map(t => tot.tramos[t.key].gs), tot.pendiente.gs, tot.pendiente.usd, tot.cobrado.gs],
    archivo: `Estado_cuenta_antiguedad_${data.fecha_corte}`,
  }, setExportando);

  const exportarDetalle = (tipo) => exportarReporte(tipo, {
    titulo: "Estado de cuenta de clientes — facturas",
    filtros: [`Al ${fechaReporte(data.fecha_corte)}`, cliente && `Cliente ${filas[0]?.cliente || ""}`,
      `${{ pendientes: "Pendientes", cobradas: "Cobradas", todas: "Todas" }[estado]}`].filter(Boolean).join(" · "),
    columnas: [{ l: "Cliente" }, { l: "N° Factura" }, { l: "N° COB" }, { l: "Condición" }, { l: "Emisión" }, { l: "Vencimiento" }, { l: "Días vencida" }, { l: "Antigüedad" },
      { l: "Total (Gs)", num: true }, { l: "Cobrado (Gs)", num: true }, { l: "Pendiente (Gs)", num: true }, { l: "Estado" },
      { l: "Moneda" }, { l: "Total USD", num: true }, { l: "Pendiente USD", num: true }],
    // Cada moneda se suma sola: las facturas en USD van solo en sus columnas en USD (como en Cuentas por Cobrar).
    filas: detalle.map(r => [r.cliente, r.factura || "Sin factura", r.cobranza, CONDICION[r.condicion] || "", fechaReporte(r.emision),
      fechaReporte(r.vence) + (r.vence_estimado ? " (est.)" : ""), r.dias_atraso || "", tramoLabel[r.tramo] || "",
      r.moneda === "USD" ? "" : r.monto_total, r.moneda === "USD" ? "" : r.monto_cobrado, r.moneda === "USD" ? "" : r.monto_pendiente,
      (ESTADO[r.estado] || [r.estado])[0], r.moneda, r.moneda === "USD" ? r.monto_usd : "", r.moneda === "USD" ? r.pendiente_usd : ""]),
    totales: ["TOTALES", "", "", "", "", "", "", "",
      detalle.reduce((a, r) => a + (r.moneda === "USD" ? 0 : r.monto_total), 0), detalle.reduce((a, r) => a + (r.moneda === "USD" ? 0 : r.monto_cobrado), 0),
      detalle.reduce((a, r) => a + (r.moneda === "USD" ? 0 : r.monto_pendiente), 0), "", "",
      detalle.reduce((a, r) => a + (r.moneda === "USD" ? r.monto_usd : 0), 0), detalle.reduce((a, r) => a + (r.moneda === "USD" ? r.pendiente_usd : 0), 0)],
    archivo: `Estado_cuenta_facturas_${data.fecha_corte}`,
  }, setExportando);

  const th = "px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide";
  return (
    <div className="space-y-5" data-testid="ec-tab">
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-start sm:items-center">
        <label className="flex items-center gap-2 text-xs font-semibold text-gray-500">Cliente
          <select value={cliente} onChange={e => setCliente(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-800 min-w-[220px]" data-testid="ec-cliente">
            <option value="">Todos los clientes</option>
            {clientes.map(c => <option key={c.cliente_clave} value={c.cliente_clave}>{c.cliente}</option>)}
          </select>
        </label>
        <div className="flex gap-2 flex-wrap">
          {[["pendientes", "Pendientes"], ["cobradas", "Cobradas"], ["todas", "Todas"]].map(([k, l]) => (
            <button key={k} onClick={() => setEstado(k)} className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all ${estado === k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`} data-testid={`ec-estado-${k}`}>{l}</button>
          ))}
        </div>
        <button onClick={cargar} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50" title="Actualizar" data-testid="ec-refresh"><RefreshCw size={14} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} /></button>
        {cliente && (
          <button onClick={() => setCuenta(clientes.find(c => c.cliente_clave === cliente))} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-xl border border-[#2E6DA4]/40 text-[#2E6DA4] hover:bg-blue-50" data-testid="ec-cuenta-corriente-btn">
            <FileText size={13} /> Cuenta corriente de este cliente
          </button>
        )}
        <p className="text-xs text-gray-400 sm:ml-auto" data-testid="ec-corte">Al {formatDate(data.fecha_corte)}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tarjeta titulo="Pendiente de cobro" color="amber" sub={`${tot.pendientes} factura${tot.pendientes === 1 ? "" : "s"} sin cobrar`} testId="ec-card-pendiente"><Monto v={tot.pendiente} /></Tarjeta>
        <Tarjeta titulo="Vencido" color="red" sub={`${pct(tot.vencido.gs)} % de lo pendiente`} testId="ec-card-vencido"><Monto v={tot.vencido} /></Tarjeta>
        <Tarjeta titulo="Cobrado" color="emerald" sub={`de ${formatPYG(tot.facturado.gs)}${tot.facturado.usd > 0 ? ` + ${formatUSD2(tot.facturado.usd)}` : ""} facturados`} testId="ec-card-cobrado"><Monto v={tot.cobrado} /></Tarjeta>
        <Tarjeta titulo="Deuda más antigua" color="slate" sub="días pasados del vencimiento" testId="ec-card-antigua">{tot.dias_max > 0 ? `${tot.dias_max} días` : "Al día"}</Tarjeta>
      </div>

      {(pendGs > 0 || tot.pendiente.usd > 0) && (
        <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm p-5" data-testid="ec-barra">
          <p className="text-sm font-semibold text-gray-700 mb-3">Antigüedad de lo pendiente</p>
          {pendGs > 0 && (
            <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 mb-4">
              {data.tramos.map(t => tot.tramos[t.key].gs > 0 && (
                <div key={t.key} className={TRAMO_BG[t.key]} style={{ width: `${(tot.tramos[t.key].gs * 100) / pendGs}%` }} title={`${t.label}: ${formatPYG(tot.tramos[t.key].gs)}`} />
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {data.tramos.map(t => (
              <div key={t.key} data-testid={`ec-tramo-${t.key}`}>
                <div className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${TRAMO_BG[t.key]}`} /><p className="text-xs font-semibold text-gray-500">{t.label}</p></div>
                <p className="text-sm font-bold text-gray-900"><Monto v={tot.tramos[t.key]} /></p>
                {pendGs > 0 && <p className="text-[11px] text-gray-400">{pct(tot.tramos[t.key].gs)} %</p>}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-3">Los días se cuentan desde el vencimiento de cada factura; si no tiene vencimiento cargado, desde la emisión + 60 días. Las facturas en USD se muestran aparte, en azul.</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-800">{cliente ? `Antigüedad de la deuda — ${filas[0]?.cliente || ""}` : "Antigüedad de la deuda por cliente"}</h3>
          <Exportar exportar={exportarResumen} exportando={exportando} testId="ec-resumen-exportar" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="ec-resumen">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className={`text-left ${th}`}>Cliente</th>
                <th className={`text-center ${th}`}>Pend.</th>
                {data.tramos.map(t => <th key={t.key} className={`text-right ${th}`}>{t.label}</th>)}
                <th className={`text-right ${th}`}>Total pendiente</th>
                <th className={`text-right ${th}`}>Cobrado</th>
                <th className={th}></th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 ? (
                <tr><td colSpan={data.tramos.length + 5} className="text-center py-10 text-gray-400 text-sm">No hay facturas cargadas</td></tr>
              ) : filas.map((c, i) => (
                <tr key={c.cliente_clave} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`ec-resumen-fila-${i}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => setCliente(c.cliente_clave)} className="text-sm font-semibold text-gray-900 hover:text-emerald-700 text-left" title="Ver solo este cliente" data-testid={`ec-cliente-${i}`}>{c.cliente}</button>
                    {c.ruc && <p className="text-[11px] text-gray-400">{c.ruc}</p>}
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-600">{c.pendientes}</td>
                  {data.tramos.map(t => <td key={t.key} className={`px-4 py-3 text-right text-sm ${TRAMO_TXT[t.key]}`} data-testid={`ec-r${i}-${t.key}`}><Monto v={c.tramos[t.key]} /></td>)}
                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-900" data-testid={`ec-r${i}-total`}><Monto v={c.pendiente} /></td>
                  <td className="px-4 py-3 text-right text-xs text-emerald-700"><Monto v={c.cobrado} /></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setCuenta(c)} className="text-[#2E6DA4] hover:bg-blue-50 px-2 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ml-auto" data-testid={`ec-cuenta-${i}`}><FileText size={12} /> Cuenta corriente</button>
                  </td>
                </tr>
              ))}
              {!cliente && filas.length > 1 && (
                <tr className="bg-gray-50 font-bold" data-testid="ec-resumen-total">
                  <td className="px-4 py-3 text-sm">Total</td>
                  <td className="px-4 py-3 text-center text-xs">{tot.pendientes}</td>
                  {data.tramos.map(t => <td key={t.key} className="px-4 py-3 text-right text-sm"><Monto v={tot.tramos[t.key]} /></td>)}
                  <td className="px-4 py-3 text-right text-sm" data-testid="ec-total-pendiente"><Monto v={tot.pendiente} /></td>
                  <td className="px-4 py-3 text-right text-xs"><Monto v={tot.cobrado} /></td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-800">Facturas ({detalle.length})</h3>
          <Exportar exportar={exportarDetalle} exportando={exportando} testId="ec-detalle-exportar" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="ec-detalle">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className={`text-left ${th}`}>Emisión</th>
                <th className={`text-left ${th}`}>Factura</th>
                <th className={`text-left ${th}`}>Cliente</th>
                <th className={`text-left ${th}`}>Condición</th>
                <th className={`text-left ${th}`}>Vence</th>
                <th className={`text-left ${th}`}>Antigüedad</th>
                <th className={`text-right ${th}`}>Total</th>
                <th className={`text-right ${th}`}>Cobrado</th>
                <th className={`text-right ${th}`}>Pendiente</th>
                <th className={`text-center ${th}`}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {detalle.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-10 text-gray-400 text-sm">No hay facturas {estado === "cobradas" ? "cobradas" : estado === "pendientes" ? "pendientes" : "cargadas"}</td></tr>
              ) : detalle.map((r, i) => {
                const usd = r.moneda === "USD";
                const [estTxt, estCls] = ESTADO[r.estado] || [r.estado, ""];
                return (
                  <tr key={r._id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`ec-detalle-fila-${i}`}>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(r.emision)}</td>
                    <td className="px-4 py-3">
                      {r.factura ? <p className="text-xs font-mono font-bold text-emerald-700">{r.factura}</p> : <p className="text-xs font-semibold text-amber-600">Sin factura</p>}
                      <p className="text-[11px] font-mono text-gray-400">{r.cobranza}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-800">{r.cliente}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{CONDICION[r.condicion] || "—"}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap" title={r.vence_estimado ? "Sin vencimiento cargado: se toma la emisión + 60 días" : ""}>
                      {r.vence ? formatDate(r.vence) : "—"}{r.vence_estimado && <span className="text-gray-400"> (est.)</span>}
                    </td>
                    <td className="px-4 py-3 text-xs" data-testid={`ec-detalle-antiguedad-${i}`}>
                      {r.tramo === "" ? <span className="text-gray-300">—</span>
                        : r.dias_atraso > 0 ? <span className={`font-semibold ${TRAMO_TXT[r.tramo]}`}>{r.dias_atraso} d vencida</span>
                        : <span className="font-semibold text-emerald-700">Al día</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-900">{formatPYG(r.monto_total)}{usd && <span className="block text-[11px] font-semibold text-blue-600">{formatUSD2(r.monto_usd)}</span>}</td>
                    <td className="px-4 py-3 text-right text-xs text-emerald-700">{formatPYG(r.monto_cobrado)}{usd && <span className="block text-[11px] text-blue-600">{formatUSD2(r.cobrado_usd)}</span>}</td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-amber-700">{formatPYG(r.monto_pendiente)}{usd && <span className="block text-[11px] font-semibold text-blue-600">{formatUSD2(r.pendiente_usd)}</span>}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-semibold border ${estCls}`}>{estTxt}</span>
                      {r.cobro_parcial && r.estado !== "parcial" && <span className="block text-[10px] text-blue-600 mt-0.5">con cobro parcial</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {cuenta && <EstadoCuentaModal tipo="cliente" nombre={cuenta.cliente} clienteId={cuenta.cliente_id} onClose={() => setCuenta(null)} testId="ec-cuenta-corriente" opciones={clientes.map(c => c.cliente)} />}
    </div>
  );
}
